/**
 * React Query hooks for audit log management
 * Provides query operations with caching and export functionality
 */

import { useQuery, useMutation } from '@tanstack/react-query'
import { get, apiClient } from '@/lib/api-client'
import type {
  AuditLogEntry,
  AuditLogListResponse,
  AuditLogQueryParams,
  AuditLogExportParams,
  ExportFormat,
} from '@/types/audit'

/**
 * Query keys for audit logs
 */
export const auditLogKeys = {
  all: ['audit-logs'] as const,
  lists: () => [...auditLogKeys.all, 'list'] as const,
  list: (params: AuditLogQueryParams) =>
    [...auditLogKeys.lists(), params] as const,
  details: () => [...auditLogKeys.all, 'detail'] as const,
  detail: (id: string) => [...auditLogKeys.details(), id] as const,
}

/**
 * Build query string from parameters
 */
function buildQueryParams(params: AuditLogQueryParams): string {
  const queryParams = new URLSearchParams()

  if (params.page !== undefined) {
    queryParams.set('page', String(params.page))
  }
  if (params.pageSize !== undefined) {
    queryParams.set('pageSize', String(params.pageSize))
  }
  if (params.startDate) {
    queryParams.set('startDate', params.startDate)
  }
  if (params.endDate) {
    queryParams.set('endDate', params.endDate)
  }
  if (params.actorId) {
    queryParams.set('actorId', params.actorId)
  }
  if (params.actorSearch) {
    queryParams.set('actorSearch', params.actorSearch)
  }
  if (params.entityType) {
    queryParams.set('entityType', params.entityType)
  }
  if (params.action) {
    queryParams.set('action', params.action)
  }
  if (params.outcome) {
    queryParams.set('outcome', params.outcome)
  }
  if (params.resourceFilter) {
    queryParams.set('resourceFilter', params.resourceFilter)
  }
  if (params.sortBy) {
    queryParams.set('sortBy', params.sortBy)
  }
  if (params.sortOrder) {
    queryParams.set('sortOrder', params.sortOrder)
  }

  return queryParams.toString()
}

/**
 * Fetch paginated list of audit logs with filters
 */
export function useAuditLogs(params: AuditLogQueryParams = {}) {
  return useQuery({
    queryKey: auditLogKeys.list(params),
    queryFn: async () => {
      const queryString = buildQueryParams(params)
      const url = queryString ? `/audit-logs?${queryString}` : '/audit-logs'
      return get<AuditLogListResponse>(url)
    },
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Fetch a single audit log entry by ID
 */
export function useAuditLog(id: string | undefined) {
  return useQuery({
    queryKey: auditLogKeys.detail(id ?? ''),
    queryFn: () => get<AuditLogEntry>(`/audit-logs/${id}`),
    enabled: !!id,
  })
}

/**
 * Generate a timestamped filename for export
 */
function generateExportFilename(format: ExportFormat): string {
  const now = new Date()
  const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19)
  return `audit-logs-${timestamp}.${format}`
}

/**
 * Export audit logs as CSV or JSON
 */
export function useExportAuditLogs() {
  return useMutation({
    mutationFn: async ({
      params,
      format,
      onProgress,
    }: {
      params: Omit<AuditLogExportParams, 'format'>
      format: ExportFormat
      onProgress?: (progress: number) => void
    }) => {
      const queryParams = new URLSearchParams()

      if (params.startDate) {
        queryParams.set('startDate', params.startDate)
      }
      if (params.endDate) {
        queryParams.set('endDate', params.endDate)
      }
      if (params.actorId) {
        queryParams.set('actorId', params.actorId)
      }
      if (params.actorSearch) {
        queryParams.set('actorSearch', params.actorSearch)
      }
      if (params.entityType) {
        queryParams.set('entityType', params.entityType)
      }
      if (params.action) {
        queryParams.set('action', params.action)
      }
      if (params.outcome) {
        queryParams.set('outcome', params.outcome)
      }
      if (params.resourceFilter) {
        queryParams.set('resourceFilter', params.resourceFilter)
      }
      queryParams.set('format', format)

      const queryString = queryParams.toString()
      const url = `/audit-logs/export?${queryString}`

      // Simulate initial progress for UX
      onProgress?.(10)

      const response = await apiClient.get(url, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round(
              10 + (progressEvent.loaded / progressEvent.total) * 80
            )
            onProgress?.(progress)
          }
        },
      })

      onProgress?.(90)

      // Create download link
      const blob = new Blob([response.data], {
        type: format === 'csv' ? 'text/csv' : 'application/json',
      })
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = generateExportFilename(format)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)

      onProgress?.(100)

      return { success: true, filename: link.download }
    },
  })
}
